import { Component, Input, OnInit, ViewChild, effect, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SplitterModule } from 'primeng/splitter';
import { ToastModule } from 'primeng/toast';
import { Header } from './components/header/header';
import { LeftPanel } from './components/left-panel/left-panel';
import { RightPanel } from './components/right-panel/right-panel';
import { UserMetaDataService } from './services/user-service';
import { FilesystemFilterService } from './services/filesystem-filter.service';
import { SystemConfigService } from './services/system-config.service';
import { FileItem, LinkPatch } from './models';
import { DocMatrixNavigationTarget, LinkedFileItem } from './models/api';

/**
 * Önálló, beágyazható wrapper komponens, ami az `attachments-handler` teljes felületét
 * (fejléc + bal/jobb panel a splitterben) egyetlen elemként teszi elérhetővé. A befogadó
 * alkalmazásnak nem kell a külső handler-appot használnia — elég a `provideAttachmentsHandler(...)`
 * a gyökér providerek között, majd:
 *
 * ```html
 * <lib-attachments-handler [path]="'tao'" />
 * ```
 *
 * A komponens kitölti a szülő magasságát, ezért a befogadónak adott magasságú konténerbe
 * (pl. `height: 100dvh`) érdemes tennie.
 */
@Component({
  selector: 'lib-attachments-handler',
  imports: [Header, LeftPanel, RightPanel, SplitterModule, ToastModule],
  templateUrl: './attachments-handler.html',
  styleUrl: './attachments-handler.scss',
})
export class AttachmentsHandler implements OnInit {
  @ViewChild('rightFilesPanel') private rightPanel!: RightPanel;
  @ViewChild('leftFilesPanel') private leftFilesPanel!: RightPanel;
  @ViewChild('fullscreenFilesPanel') private fullscreenFilesPanel?: RightPanel;
  @ViewChild(LeftPanel) private leftPanel!: LeftPanel;

  private readonly userService = inject(UserMetaDataService);
  private readonly filesystemFilterService = inject(FilesystemFilterService);
  private readonly systemConfigService = inject(SystemConfigService);
  private readonly titleService = inject(Title);

  constructor() {
    effect(() => {
      if (!this.updateDocumentTitle) return;
      const title = this.systemConfigService.title();
      if (title) {
        this.titleService.setTitle(title);
      }
    });
  }

  /**
   * A gyökér munkaterület (filesystem) azonosítója, amivel a panelek indulnak.
   * A megadott érték `<path>://` formára egészül ki. Alapértelmezés: `tao`.
   */
  @Input() set path(value: string) {
    this.currentPath = value ? `${value}://` : 'tao://';
    this.leftPath = this.currentPath;
  }

  /**
   * Ha `true`, a rendszer-konfigurációból érkező címet a böngészőfül címeként is beállítja.
   * Beágyazott használatnál általában nem kívánatos, ezért alapból ki van kapcsolva.
   */
  @Input() updateDocumentTitle = false;

  currentPath = 'tao://';
  leftPath = 'tao://';
  isDraggingFile = false;
  hasSelectedCategory = false;
  allLinks: LinkedFileItem[] = [];
  currentSourcemodelId: number | null = null;
  splitterSizes = signal<number[]>([50, 50]);
  matrixExpanded = signal(false);
  leftView = signal<'matrix' | 'files'>('matrix');
  hasMatrix = signal(true);
  locked = signal(false);

  ngOnInit(): void {
    this.userService.load();
    this.filesystemFilterService.load();
    this.systemConfigService.load();
  }

  onFilesLinked(patch: LinkPatch): void {
    this.rightPanel.applyLinkPatch(patch);
    this.leftFilesPanel?.applyLinkPatch(patch);
    this.fullscreenFilesPanel?.applyLinkPatch(patch);
  }

  onLinkFailed(): void {
    this.rightPanel.refreshIfVisible();
    this.leftFilesPanel?.refreshIfVisible();
    this.fullscreenFilesPanel?.refreshIfVisible();
  }

  onValidate(): void {
    this.leftPanel.validateAttachments();
  }

  onFinalize(): void {
    this.leftPanel.finalizeAttachments();
  }

  onReopen(): void {
    this.leftPanel.reopenAttachments();
  }

  onCategorySelected(selected: boolean): void {
    this.hasSelectedCategory = selected;
  }

  onMatrixEmptyChange(empty: boolean): void {
    this.hasMatrix.set(!empty);
    if (empty) {
      this.leftView.set('files');
    }
  }

  onLinkSelected(files: FileItem[]): void {
    this.leftPanel.linkFiles(files);
  }

  onScanComplete(): void {
    this.leftPanel.refreshLinkedFiles();
  }

  onAllLinksChange(links: LinkedFileItem[]): void {
    this.allLinks = links;
  }

  onSettlementChange(sourcemodelId: number): void {
    this.currentSourcemodelId = sourcemodelId;
  }

  onNavigateToFile({ path, fileName }: { path: string; fileName: string }): void {
    this.rightPanel.navigateToPath(path, fileName);
  }

  onPreviewFile({ path, fileName }: { path: string; fileName: string }): void {
    this.rightPanel.previewByPath(path, fileName);
  }

  onPreviewVisibleChange(visible: boolean): void {
    this.splitterSizes.set(visible ? [30, 70] : [50, 50]);
  }

  onNavigateToDocMatrixItem({ itemUuid, sourcemodelId }: DocMatrixNavigationTarget): void {
    this.leftView.set('matrix');
    this.leftPanel.highlightDocMatrixItem(itemUuid, sourcemodelId);
  }

  onFilesMoved(sourcePanelId: string): void {
    const sourcePanel = sourcePanelId === 'left'
      ? this.leftFilesPanel
      : sourcePanelId === 'fullscreen'
        ? this.fullscreenFilesPanel
        : this.rightPanel;
    sourcePanel?.refreshAfterExternalChange();
    this.leftPanel.refreshLinkedFiles();
  }
}
